(() => {
  'use strict';
  if (customElements.get('miniami-pdp-drift')) return;
  customElements.define('miniami-pdp-drift', class extends HTMLElement {
    connectedCallback() {
      if (this.dispose) return;
      const viewport = this.querySelector('.miniami-pdp-drift__viewport');
      const primary = this.querySelector('[data-pdp-drift-primary]');
      if (!viewport || !primary || !primary.children.length) return;
      const abort = new AbortController();
      const on = (target, type, fn, opts = {}) => target?.addEventListener(type, fn, { ...opts, signal: abort.signal });
      const reduce = matchMedia('(prefers-reduced-motion: reduce)');
      const clones = [];
      let frame = 0, last = 0, position = 0, written = 0, touching = false, intersecting = false, idleUntil = 0, motionEnabled = null;
      let mouse = null, suppressedClick = null, nativeTouches = 0;
      const copies = Math.max(4, Math.ceil(viewport.clientWidth / Math.max(primary.getBoundingClientRect().width, 1)) + 4);
      for (let i = 0; i < Math.min(copies, 12); i++) {
        const clone = primary.cloneNode(true);
        clone.removeAttribute('data-pdp-drift-primary');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(el => { el.tabIndex = -1; });
        viewport.appendChild(clone);
        clones.push(clone);
      }
      const span = () => primary.getBoundingClientRect().width;
      // C66 expands the old eight-card source rail. Keep its reading pace
      // instead of accelerating every new pattern into the same58-second lap.
      const cycleMs = 58000 * Math.max(1, primary.children.length / 8);
      const allowed = () => !reduce.matches && !navigator.connection?.saveData && !document.hidden
        && !document.documentElement.dataset.miniamiMotionForcedStatic?.includes('true')
        && document.documentElement.dataset.miniamiMotionPaused !== 'true'
        && (document.body.dataset.miniamiMotionLevel || document.body.dataset.miniamiMotion || '').toLowerCase() !== 'off';
      const stop = () => { if (frame) cancelAnimationFrame(frame); frame = 0; last = 0; };
      const normalize = () => {
        const width = span();
        if (!width) return;
        // Mirrors form a buffer on both sides. Recenter only after native inertia
        // has ended; never write scrollLeft while a finger/fling is in control.
        if (position < width || position >= 3 * width) {
          position = 2 * width + ((position % width) + width) % width;
          viewport.scrollLeft = position;
          written = viewport.scrollLeft;
        }
      };
      const tick = time => {
        frame = 0;
        if (!this.isConnected || !intersecting || !allowed()) { stop(); return; }
        if (!touching && !mouse && time >= idleUntil) {
          normalize();
          if (last) position += (this.dataset.direction === 'right' ? -1 : 1) * Math.min(time - last, 50) * span() / cycleMs;
          viewport.scrollLeft = position;
          written = viewport.scrollLeft;
        } else position = viewport.scrollLeft;
        last = time;
        frame = requestAnimationFrame(tick);
      };
      const sync = () => {
        const enabled = allowed();
        if (motionEnabled !== enabled) {
          const width = span(), offset = width ? ((viewport.scrollLeft % width) + width) % width : 0;
          clones.forEach(clone => { clone.hidden = !enabled; });
          position = enabled ? 2 * width + offset : offset;
          viewport.scrollLeft = position;
          written = viewport.scrollLeft;
          motionEnabled = enabled;
          last = 0;
        }
        this.dataset.motionStatic = String(!enabled || !intersecting);
        if (!enabled || !intersecting) stop(); else if (!frame) frame = requestAnimationFrame(tick);
      };
      const takeover = () => { idleUntil = performance.now() + 1100; position = viewport.scrollLeft; };
      const release = () => { if (!touching || nativeTouches) return; touching = false; takeover(); sync(); };
      const releaseMouse = e => {
        if (!mouse || (e.pointerId != null && e.pointerId !== mouse.id)) return;
        const ended = mouse;
        mouse = null;
        suppressedClick = e.type === 'pointerup' && ended.dragged ? ended.id : null;
        if (viewport.hasPointerCapture(ended.id)) viewport.releasePointerCapture(ended.id);
        takeover();
        sync();
      };
      viewport.scrollLeft = position = written = 2 * span();
      // Hover/focus alone never stop drift. Only a real input takes over.
      on(window, 'pointerdown', () => { suppressedClick = null; }, { capture: true });
      on(viewport, 'pointerdown', e => {
        if (e.pointerType !== 'mouse') { touching = true; takeover(); return; }
        if (e.button !== 0 || !e.isPrimary) return;
        mouse = { id: e.pointerId, x: e.clientX, y: e.clientY, start: viewport.scrollLeft, dragged: false };
        takeover();
      }, { passive: true });
      on(window, 'pointermove', e => {
        if (!mouse || e.pointerId !== mouse.id) return;
        if (!(e.buttons & 1)) { releaseMouse(e); return; }
        const dx = e.clientX - mouse.x, dy = e.clientY - mouse.y;
        if (!mouse.dragged) {
          if (Math.abs(dx) < 6 || Math.abs(dx) <= Math.abs(dy)) return;
          mouse.dragged = true;
          viewport.setPointerCapture(mouse.id);
        }
        e.preventDefault();
        viewport.scrollLeft = position = mouse.start - dx;
        written = viewport.scrollLeft;
      }, { passive: false });
      // Native image/link dragging would steal the pointer before our threshold.
      on(viewport, 'dragstart', e => { if (mouse) e.preventDefault(); });
      on(viewport, 'click', e => {
        if (suppressedClick === null || e.detail === 0) return;
        suppressedClick = null;
        e.preventDefault();
        e.stopImmediatePropagation();
      }, { capture: true });
      on(window, 'pointerup', e => { releaseMouse(e); release(); }, { passive: true });
      on(window, 'pointercancel', e => { releaseMouse(e); release(); }, { passive: true });
      on(viewport, 'lostpointercapture', releaseMouse, { passive: true });
      on(window, 'blur', e => { nativeTouches = 0; releaseMouse(e); release(); suppressedClick = null; });
      on(viewport, 'touchstart', e => { nativeTouches = e.touches.length; touching = true; takeover(); }, { passive: true });
      const releaseTouch = e => { nativeTouches = e.touches.length; release(); };
      on(viewport, 'touchend', releaseTouch, { passive: true });
      on(viewport, 'touchcancel', releaseTouch, { passive: true });
      on(viewport, 'scroll', () => { if (Math.abs(viewport.scrollLeft - written) > 1) takeover(); }, { passive: true });
      on(viewport, 'wheel', takeover, { passive: true });
      on(viewport, 'keydown', e => {
        takeover();
        if (e.target !== viewport || !['ArrowLeft','ArrowRight','Home','End'].includes(e.key)) return;
        e.preventDefault();
        const direction = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
        viewport.scrollBy({ left: direction * (primary.firstElementChild.getBoundingClientRect().width + 14), behavior: 'auto' });
        if (!direction) viewport.scrollLeft = motionEnabled
          ? (e.key === 'Home' ? span() : 3 * span())
          : (e.key === 'Home' ? 0 : viewport.scrollWidth - viewport.clientWidth);
        takeover();
      });
      on(document, 'visibilitychange', sync);
      on(reduce, 'change', sync);
      on(navigator.connection, 'change', sync);
      const motionObserver = new MutationObserver(sync);
      motionObserver.observe(document.documentElement, { attributes:true, attributeFilter:['data-miniami-motion-forced-static','data-miniami-motion-paused'] });
      motionObserver.observe(document.body, { attributes:true, attributeFilter:['data-miniami-motion-level','data-miniami-motion'] });
      const visibility = new IntersectionObserver(entries => { intersecting = entries[0].isIntersecting; sync(); }, { threshold:0.01 });
      visibility.observe(this);
      const resize = new ResizeObserver(() => { position = viewport.scrollLeft; takeover(); });
      resize.observe(primary);
      this.dataset.driftReady = 'true';
      this.dispose = () => { stop(); abort.abort(); if (mouse && viewport.hasPointerCapture(mouse.id)) viewport.releasePointerCapture(mouse.id); mouse = null; suppressedClick = null; visibility.disconnect(); motionObserver.disconnect(); resize.disconnect(); clones.forEach(clone => clone.remove()); delete this.dataset.driftReady; this.dispose = null; };
      sync();
    }
    disconnectedCallback() { this.dispose?.(); }
  });
})();
